import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { RefreshCw, Users as UsersIcon, Trophy, Banknote, Moon, Sun, Phone } from "lucide-react";

const TABS = [
  { key: "today",     label: "Bugün",        icon: <Sun size={14} strokeWidth={2.5} /> },
  { key: "top_count", label: "En Çok Sipariş", icon: <Trophy size={14} strokeWidth={2.5} /> },
  { key: "top_spend", label: "En Yüksek Ciro", icon: <Banknote size={14} strokeWidth={2.5} /> },
  { key: "sleeping",  label: "30+ Gün Sessiz", icon: <Moon size={14} strokeWidth={2.5} /> },
  { key: "all",       label: "Tüm Müşteriler", icon: <UsersIcon size={14} strokeWidth={2.5} /> },
];

export default function Customers() {
  const [tab, setTab] = useState("today");
  const [stats, setStats] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [history, setHistory] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/customers-stats"),
        api.get("/customers"),
      ]);
      setStats(s.data);
      setAll(a.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const list = tab === "all" ? all : (stats?.[tab] || []);

  const openHistory = async (c) => {
    setOpenId(c.id);
    setHistory(null);
    try {
      const { data } = await api.get(`/customers/${c.id}/orders`);
      setHistory({ customer: c, orders: data });
    } catch (e) { toast.error(formatApiError(e)); setOpenId(null); }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader
        title="Müşteriler"
        subtitle="Müşteri Veritabanı"
        right={
          <button
            data-testid="refresh-button"
            onClick={fetchAll}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 border-2 border-zinc-200 hover:border-zinc-950 transition-colors text-xs font-bold uppercase tracking-wider"
          >
            <RefreshCw size={14} strokeWidth={2.5} /> Yenile
          </button>
        }
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-5 flex gap-2 overflow-x-auto no-scrollbar">
          {TABS.map((t) => {
            const count = t.key === "all" ? all.length : (stats?.[t.key]?.length || 0);
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${
                  active ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-950"
                }`}
              >
                {t.icon} {t.label} <span className={`font-mono ${active ? "text-white/80" : "text-zinc-500"}`}>({count})</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <Empty text="Yükleniyor…" />
        ) : list.length === 0 ? (
          <Empty text="Bu kategoride müşteri yok." />
        ) : (
          <ul className="space-y-3" data-testid="customers-list">
            {list.map((c) => (
              <li
                key={c.id}
                data-testid={`customer-row-${c.id}`}
                className="bg-white border-2 border-zinc-200 brutal-shadow p-4 sm:p-5"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      data-testid={`customer-name-${c.id}`}
                      onClick={() => openHistory(c)}
                      className="text-lg font-black tracking-tight text-zinc-950 hover:underline text-left"
                    >
                      {c.full_name}
                    </button>
                    <div className="mt-1">
                      <a href={`tel:${c.phone}`} data-testid={`customer-call-${c.id}`} className="inline-flex items-center gap-1 font-mono text-sm text-zinc-700 hover:underline">
                        <Phone size={13} strokeWidth={2.5} />
                        {c.phone}
                      </a>
                    </div>
                    <div className="mt-1 text-sm text-zinc-600 line-clamp-2">{c.address}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 sm:w-[420px] shrink-0">
                    <Stat label="Sipariş" value={c.total_orders} testId={`stat-count-${c.id}`} />
                    <Stat label="Toplam" value={`₺${c.total_amount.toFixed(0)}`} testId={`stat-total-${c.id}`} />
                    <Stat label="Ortalama" value={`₺${c.avg_amount.toFixed(0)}`} testId={`stat-avg-${c.id}`} />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  <span>İlk: {formatDateTime(c.first_order_at)}</span>
                  <span>· Son: {formatDateTime(c.last_order_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {openId && history && (
        <HistoryModal customer={history.customer} orders={history.orders} onClose={() => { setOpenId(null); setHistory(null); }} />
      )}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-zinc-50 border-2 border-zinc-200 px-3 py-2 text-center">
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="text-lg font-black tracking-tighter text-zinc-950 mt-0.5">{value}</div>
    </div>
  );
}

function HistoryModal({ customer, orders, onClose }) {
  return (
    <div
      data-testid="customer-history-modal"
      className="fixed inset-0 z-50 bg-zinc-950/70 flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div className="bg-white border-2 border-zinc-950 brutal-shadow-dark w-full sm:max-w-xl my-0 sm:my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b-2 border-zinc-200 px-5 py-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Müşteri</div>
            <div className="text-lg font-black tracking-tight text-zinc-950 break-words">{customer.full_name}</div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-zinc-950 text-2xl">×</button>
        </div>
        <ul className="max-h-[60vh] overflow-y-auto">
          {orders.length === 0 ? (
            <li className="p-6 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Sipariş yok</li>
          ) : (
            orders.map((o) => (
              <li key={o.id} className="px-5 py-3 border-b border-zinc-100 last:border-b-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-bold text-zinc-950 text-sm">{formatDateTime(o.delivery_time)}</div>
                    <div className="text-xs text-zinc-600 truncate">{summarizeCustomerOrder(o)}</div>
                  </div>
                  <div className="text-right shrink-0 font-mono text-[10px] uppercase tracking-wider text-zinc-700">
                    {o.status} {o.amount > 0 && `· ₺${o.amount.toFixed(0)}`}
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

function summarizeCustomerOrder(o) {
  if (Array.isArray(o.items) && o.items.length) return o.items.join(" • ");
  if (Array.isArray(o.notes) && o.notes.length) return o.notes.join(" • ");
  return o.description || "—";
}

function Empty({ text }) {
  return (
    <div className="border-2 border-dashed border-zinc-200 bg-white p-10 text-center">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">{text}</div>
    </div>
  );
}
